// src/components/layout/UI.js
// All components use CSS variables set by ThemeContext so they auto-adapt to light/dark
import React, { useState } from 'react';
import { useResponsive } from '../../hooks/useResponsive';

// ── Card ──────────────────────────────────────────────────────────────────────
export function Card({ children, style, title, action }) {
  return (
    <div style={{ background: 'var(--bgCard)', border: '1px solid var(--border)', borderRadius: 14, padding: '20px 22px', position: 'relative', ...style }}>
      {(title || action) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          {title && <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 14, color: 'var(--textPrimary)', letterSpacing: '-0.2px' }}>{title}</div>}
          {action && <div>{action}</div>}
        </div>
      )}
      {children}
    </div>
  );
}

// ── StatCard ──────────────────────────────────────────────────────────────────
export function StatCard({ label, value, sub, icon, color = '#f97316', trend }) {
  return (
    <div style={{ background: 'var(--bgCard)', border: '1px solid var(--border)', borderRadius: 14, padding: '20px 22px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, borderRadius: 14, background: color + '06', pointerEvents: 'none' }} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ width: 38, height: 38, borderRadius: 10, background: color + '18', color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>{icon}</div>
        {trend !== undefined && (
          <span style={{ fontSize: 11, color: trend >= 0 ? '#34d399' : '#f87171', fontWeight: 600 }}>
            {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}%
          </span>
        )}
      </div>
      <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 30, color: 'var(--textPrimary)', letterSpacing: '-1px', marginBottom: 4, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--textMuted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--textFaint)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────────
export function Modal({ open, onClose, title, children, width = 560 }) {
  const { isMobile } = useResponsive();
  if (!open) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--bgOverlay)', display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', zIndex: 1000, padding: isMobile ? 0 : 20, backdropFilter: 'blur(4px)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: 'var(--bgModal)',
        border: isMobile ? 'none' : '1px solid var(--borderInput)',
        borderRadius: isMobile ? '18px 18px 0 0' : 16,
        width: '100%',
        maxWidth: isMobile ? '100%' : width,
        boxShadow: 'var(--shadow)',
        animation: isMobile ? 'sheetUp 0.25s ease' : 'modalIn 0.2s ease',
        maxHeight: isMobile ? '92vh' : '90vh',
        overflow: 'auto',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: isMobile ? '16px 18px' : '20px 24px', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bgModal)', zIndex: 1 }}>
          <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: isMobile ? 15 : 16, color: 'var(--textPrimary)' }}>{title}</div>
          <button onClick={onClose} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 7, color: 'var(--textMuted)', cursor: 'pointer', width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, flexShrink: 0 }}>✕</button>
        </div>
        <div style={{ padding: isMobile ? '18px' : '22px 24px' }}>{children}</div>
      </div>
      <style>{`@keyframes sheetUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>
    </div>
  );
}

// ── Table ─────────────────────────────────────────────────────────────────────
// On mobile, renders each row as a stacked card (first column as the card
// "title", remaining labeled columns as key/value rows, unlabeled columns
// — typically action buttons — rendered full-width at the bottom).
export function Table({ columns, data, onRowClick, emptyMessage = 'No data found' }) {
  const { isMobile } = useResponsive();

  if (isMobile) {
    if (data.length === 0) {
      return (
        <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--textFaint)', fontSize: 13, background: 'var(--bgCard)', border: '1px solid var(--border)', borderRadius: 12 }}>
          {emptyMessage}
        </div>
      );
    }
    const [titleCol, ...restCols] = columns;
    // By convention across this app, the row-actions column always uses key 'id'
    // (holding buttons), regardless of whether it has a visible label like "Actions".
    const labeledCols = restCols.filter(c => c.key !== 'id');
    const actionCols = restCols.filter(c => c.key === 'id');

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {data.map((row, i) => (
          <div
            key={row.id || i}
            onClick={() => onRowClick?.(row)}
            style={{ background: 'var(--bgCard)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px', cursor: onRowClick ? 'pointer' : 'default' }}
          >
            <div style={{ marginBottom: labeledCols.length ? 10 : 0 }}>
              {titleCol.render ? titleCol.render(row[titleCol.key], row) : row[titleCol.key]}
            </div>
            {labeledCols.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px', paddingTop: 10, borderTop: '1px solid var(--border)' }}>
                {labeledCols.map(col => (
                  <div key={col.key} style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--textFaint)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 3 }}>{col.label}</div>
                    <div style={{ fontSize: 13, color: 'var(--textSecondary)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—')}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {actionCols.length > 0 && (
              <div onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 12, marginTop: 10, borderTop: '1px solid var(--border)' }}>
                {actionCols.map(col => (
                  <div key={col.key} style={{ flex: 1 }}>
                    {col.render ? col.render(row[col.key], row) : row[col.key]}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div style={{ overflowX: 'auto', borderRadius: 12, border: '1px solid var(--border)' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            {columns.map(col => (
              <th key={col.key} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: 'var(--textFaint)', textTransform: 'uppercase', letterSpacing: '0.8px', background: 'var(--bgTableHead)', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap', ...col.style }}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr><td colSpan={columns.length} style={{ padding: '40px', textAlign: 'center', color: 'var(--textFaint)', fontSize: 13 }}>{emptyMessage}</td></tr>
          ) : data.map((row, i) => (
            <tr key={row.id || i}
              style={{ borderBottom: '1px solid var(--border)', cursor: onRowClick ? 'pointer' : 'default', transition: 'background 0.12s' }}
              onClick={() => onRowClick?.(row)}>
              {columns.map(col => (
                <td key={col.key} style={{ padding: '11px 14px', color: 'var(--textSecondary)', verticalAlign: 'middle', whiteSpace: 'nowrap', ...col.style }}>
                  {col.render ? col.render(row[col.key], row) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Badge ─────────────────────────────────────────────────────────────────────
export function Badge({ children, color = '#6b7a9a', bg }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600, color, background: bg || color + '18', border: `1px solid ${color}30` }}>
      {children}
    </span>
  );
}

// ── Button ────────────────────────────────────────────────────────────────────
export function Button({ children, onClick, variant = 'primary', size = 'md', disabled, style: extStyle, icon }) {
  const variants = {
    primary:   { background: 'linear-gradient(135deg, #f97316, #ea580c)', color: '#fff', border: 'none' },
    secondary: { background: 'var(--bgTag)', color: 'var(--textSecondary)', border: '1px solid var(--border)' },
    ghost:     { background: 'var(--bgHover)', color: 'var(--textSecondary)', border: '1px solid var(--border)' },
    danger:    { background: 'rgba(239,68,68,0.12)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)' },
    success:   { background: 'rgba(16,185,129,0.12)', color: '#34d399', border: '1px solid rgba(16,185,129,0.3)' },
  };
  const sizes = {
    sm: { padding: '6px 12px', fontSize: 12 },
    md: { padding: '9px 16px', fontSize: 13 },
    lg: { padding: '12px 22px', fontSize: 14 },
  };
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1, fontFamily: 'DM Sans, sans-serif', fontWeight: 600, borderRadius: 9, transition: 'all 0.15s', whiteSpace: 'nowrap', ...variants[variant], ...sizes[size], ...extStyle }}>
      {icon && <span style={{ marginRight: 6 }}>{icon}</span>}
      {children}
    </button>
  );
}

// ── FormField ─────────────────────────────────────────────────────────────────
export function FormField({ label, children, required, hint }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--textMuted)', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
        {label}{required && <span style={{ color: '#f97316', marginLeft: 2 }}>*</span>}
      </label>
      {children}
      {hint && <div style={{ fontSize: 11, color: 'var(--textFaint)' }}>{hint}</div>}
    </div>
  );
}

// ── Input ─────────────────────────────────────────────────────────────────────
export function Input({ value, onChange, placeholder, type = 'text', disabled, required, min, max }) {
  const [focused, setFocused] = useState(false);
  return (
    <input type={type} value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} disabled={disabled} required={required} min={min} max={max}
      style={{ background: 'var(--bgInput)', border: `1.5px solid ${focused ? 'var(--borderFocus)' : 'var(--borderInput)'}`, borderRadius: 9, padding: '10px 12px', color: 'var(--textPrimary)', fontSize: 13, outline: 'none', width: '100%', fontFamily: 'DM Sans, sans-serif', transition: 'border-color 0.2s', opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'text' }}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} />
  );
}

// ── Select ────────────────────────────────────────────────────────────────────
export function Select({ value, onChange, options, placeholder, disabled }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
      style={{ background: 'var(--bgInput)', border: '1.5px solid var(--borderInput)', borderRadius: 9, padding: '10px 12px', color: 'var(--textPrimary)', fontSize: 13, outline: 'none', width: '100%', fontFamily: 'DM Sans, sans-serif', cursor: 'pointer' }}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
    </select>
  );
}

// ── SearchInput ───────────────────────────────────────────────────────────────
export function SearchInput({ value, onChange, placeholder = 'Search...' }) {
  return (
    <div style={{ position: 'relative' }}>
      <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--textFaint)', fontSize: 13 }}>🔍</span>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ background: 'var(--bgInput)', border: '1.5px solid var(--borderInput)', borderRadius: 9, padding: '10px 12px 10px 32px', color: 'var(--textPrimary)', fontSize: 13, outline: 'none', width: '100%', fontFamily: 'DM Sans, sans-serif' }} />
    </div>
  );
}

// ── ConfirmDialog ─────────────────────────────────────────────────────────────
export function ConfirmDialog({ open, onClose, onConfirm, title, message }) {
  const [deleting, setDeleting] = useState(false);
  if (!open) return null;

  const handleConfirm = async () => {
    setDeleting(true);
    try {
      await onConfirm(); // waits for the real delete to actually finish
    } finally {
      setDeleting(false);
      onClose();
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--bgOverlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001, backdropFilter: 'blur(4px)' }}>
      <div style={{ background: 'var(--bgModal)', border: '1px solid var(--borderInput)', borderRadius: 16, maxWidth: 400, width: '90%', boxShadow: 'var(--shadow)', animation: 'modalIn 0.2s ease' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 16, color: '#f87171' }}>⚠ {title}</div>
          <button onClick={onClose} disabled={deleting} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 7, color: 'var(--textMuted)', cursor: deleting ? 'default' : 'pointer', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, opacity: deleting ? 0.4 : 1 }}>✕</button>
        </div>
        <div style={{ padding: '22px 24px' }}>
          <p style={{ color: 'var(--textSecondary)', fontSize: 14, marginBottom: 24 }}>{message}</p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={onClose} disabled={deleting}>Cancel</Button>
            <Button variant="danger" onClick={handleConfirm} disabled={deleting}>
              {deleting ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ width: 12, height: 12, border: '2px solid rgba(248,113,113,0.3)', borderTopColor: '#f87171', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
                  Deleting...
                </span>
              ) : 'Delete'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── FuelBar ───────────────────────────────────────────────────────────────────
export function FuelBar({ level }) {
  const color = level > 50 ? '#34d399' : level > 25 ? '#f59e0b' : '#ef4444';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 5, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${level}%`, height: '100%', background: color, borderRadius: 3, transition: 'width 0.5s ease' }} />
      </div>
      <span style={{ fontSize: 11, color, fontWeight: 600, minWidth: 28 }}>{level}%</span>
    </div>
  );
}

// ── ResponsiveGrid ────────────────────────────────────────────────────────────
// A CSS grid that automatically reduces its column count on smaller screens.
// Pass the desktop column count; tablet/mobile counts default sensibly but
// can be overridden.
export function ResponsiveGrid({ children, columns = 4, tabletColumns, mobileColumns = 1, gap = 12, style }) {
  const { isMobile, isTablet } = useResponsive();
  const tablet = tabletColumns ?? Math.min(columns, 2);
  const cols = isMobile ? mobileColumns : isTablet ? tablet : columns;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap, ...style }}>
      {children}
    </div>
  );
}

// ── Toggle ────────────────────────────────────────────────────────────────────
export function Toggle({ checked, onChange, label }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
      <div onClick={() => onChange(!checked)} style={{ width: 44, height: 24, borderRadius: 12, background: checked ? '#f97316' : 'var(--border)', position: 'relative', transition: 'background 0.2s', cursor: 'pointer', flexShrink: 0 }}>
        <div style={{ position: 'absolute', top: 2, left: checked ? 22 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }} />
      </div>
      {label && <span style={{ fontSize: 13, color: 'var(--textSecondary)' }}>{label}</span>}
    </label>
  );
}
